import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
   useGetOrganizationCampaignOverview,
} from '../../hooks'
import CampaignService from '@forwardflow/services/CampaignService'
import Icon from '@forwardflow/ui/Icons'
import { Skeleton } from '@forwardflow/ui-v2'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ActivityEventsCard } from './ActivityEventsCard'
import { CampaignFilterDetails } from './CampaignFilterDetails'
// Hidden from UI for now (kept for later) — see render site below.
// import { CampaignStepTypesCard } from './CampaignStepTypesCard'
import { CampaignTypeCard } from './CampaignTypeCard'
import { LeadTypeFilterCard } from './LeadTypeFilterCard'
import { AdvanceTargetingCampaignModal } from '@forwardflow/modals/AdvanceTargetingCampaignModal'
import { NewLeadCampaignModal } from '@forwardflow/modals/NewLeadsCampaignModal'

export const LeadSourceInfo = () => {
   const [estimatedLead, setEstimatedLead] = useState<number>()
   const { title: leadSource } = useParams()
   const { data: campaignOverview, isLoading } =
      useGetOrganizationCampaignOverview(leadSource as string)

   const { data: activeLeadSourceData, isLoading: isActiveLeadSourceLoading } =
      useGetActiveLeadSource(leadSource as string)

   // Draft snapshot (draft-if-exists, else live) so unpublished campaign-type/criteria edits show.
   const { data: draftDetails, mutate: mutateDraftDetails } =
      useGetActiveLeadSourceDetails(leadSource as string)

   // Re-fetch the draft whenever the live draft state changes (e.g. campaign type toggled to/from
   // activity in another card) so this conditional reflects the draft without a publish.
   const draftSignature = `${activeLeadSourceData?.draft?.lastUpdated ?? ''}|${(
      activeLeadSourceData?.draft?.changedSections ?? []
   ).join(',')}`
   useEffect(() => {
      mutateDraftDetails()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [draftSignature])

   const campaignFilters =
      activeLeadSourceData?.data?.campaignFilters?.eligibleCriteria

   const campaignType =
      draftDetails?.data?.campaignType ??
      activeLeadSourceData?.data?.campaignType
   const isActivityCampaign = Array.isArray(campaignType)
      ? campaignType.includes('activity')
      : campaignType === 'activity'

   useEffect(() => {
      const fetchEstimatedLeads = async () => {
         if (!campaignFilters) return
         try {
            const estimatedLeadCount =
               await CampaignService.getEstimatedLeadsForCampaign(
                  campaignFilters
               )
            setEstimatedLead(estimatedLeadCount?.count || 0)
         } catch (error) {
            console.error('Error fetching estimated leads:', error)
         }
      }

      fetchEstimatedLeads()
   }, [campaignFilters, isActiveLeadSourceLoading])

   const hasNoData =
      (!campaignOverview && !isLoading) ||
      ((!estimatedLead || estimatedLead === 0) &&
         (!campaignOverview?.topAreaCodes ||
            campaignOverview?.topAreaCodes.length === 0))

   const sortedAreaCodes = campaignOverview?.topAreaCodes
      ?.sort(
         (a: { percentage: number }, b: { percentage: number }) =>
            b.percentage - a.percentage
      )
      .slice(0, 3)

   return (
      <div className="w-[350px] shrink-0 bg-[#9061DA]/[0.05] rounded-xl font-medium flex flex-col overflow-hidden">
         {/* Header */}
         <div className="flex items-center gap-x-2 px-6 py-5 shrink-0">
            <Icon name="Filters" className="scale-75 opacity-50" />
            <span className="text-white text-sm">Lead Source Info</span>
         </div>

         {/* Scrollable content */}
         <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
            {/* Campaign Type */}
            <CampaignTypeCard />

            {/* Activity events + pond triggers (only for activity campaigns) */}
            {isActivityCampaign && <ActivityEventsCard />}

            {/* Campaign step types — optional allow-list, applies to all campaign types.
                Hidden from UI for now (code kept); re-enable by uncommenting the import + this line. */}
            {/* <CampaignStepTypesCard /> */}

            {/* Lead Type Filter */}
            <LeadTypeFilterCard />

            {/* Campaign Filters */}
            <CampaignFilterDetails />

            {/* Processing message */}
            {hasNoData && !isActiveLeadSourceLoading && (
               <div className="rounded-lg bg-white/[0.05] px-5 py-4">
                  <p className="text-white/50 text-xs leading-relaxed">
                     We're still processing your lead data. Some analytics may
                     not be available yet.
                  </p>
               </div>
            )}

            {/* Estimated Leads Card */}
            {isActiveLeadSourceLoading && typeof estimatedLead !== 'number' ? (
               <div className="rounded-lg bg-white/[0.05] p-5">
                  <Skeleton height={12} width={120} radius="sm" />
                  <div className="mt-3">
                     <Skeleton height={28} width={80} radius="sm" />
                  </div>
               </div>
            ) : (
               typeof estimatedLead === 'number' &&
               estimatedLead > 0 && (
                  <div className="rounded-lg bg-white/[0.05] p-5">
                     <div className="flex items-center justify-between">
                        <p className="text-white/50 text-xs">
                           Total Estimated Leads
                        </p>
                        <Icon
                           name="Filters"
                           className="scale-75 fill-white/20"
                        />
                     </div>
                     <p className="text-white text-2xl font-semibold mt-1">
                        {estimatedLead.toLocaleString()}
                     </p>
                  </div>
               )
            )}

            {/* Top Area Codes */}
            {isLoading ? (
               <div className="rounded-lg bg-white/[0.05] p-5 space-y-4">
                  <Skeleton height={12} width={100} radius="sm" />
                  {[...Array(3)].map((_, i) => (
                     <div key={i} className="flex items-center justify-between">
                        <Skeleton height={16} width={50} radius="sm" />
                        <Skeleton height={8} width="60%" radius="xl" />
                        <Skeleton height={14} width={35} radius="sm" />
                     </div>
                  ))}
               </div>
            ) : (
               sortedAreaCodes &&
               sortedAreaCodes.length > 0 && (
                  <div className="rounded-lg bg-white/[0.05] p-5">
                     <div className="flex items-center justify-between mb-4">
                        <p className="text-white/50 text-xs">Top Area Codes</p>
                        <Icon
                           name="Location"
                           className="scale-75 fill-white/20"
                        />
                     </div>
                     <div className="space-y-3">
                        {sortedAreaCodes.map(
                           (locality: {
                              areaCode: string
                              percentage: number
                           }) => (
                              <div
                                 key={locality.areaCode}
                                 className="flex items-center gap-x-3"
                              >
                                 <span className="text-white text-sm font-semibold w-10 shrink-0">
                                    {locality.areaCode}
                                 </span>
                                 <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                       className="h-full rounded-full bg-[#9061DA]"
                                       style={{
                                          width: `${locality.percentage}%`,
                                       }}
                                    />
                                 </div>
                                 <span className="text-white/60 text-xs w-10 text-right shrink-0">
                                    {locality.percentage}%
                                 </span>
                              </div>
                           )
                        )}
                     </div>
                  </div>
               )
            )}
         </div>

         <AdvanceTargetingCampaignModal />
         <NewLeadCampaignModal />
      </div>
   )
}
